/**
 * Role-based Access Control for NWGSV Modules
 * Paid modules additionally require an active entitlement for CUSTOMER users.
 * Backend authorization is authoritative; this layer is UX-only.
 */
import type { ModuleId } from '../types';
import type { UserRole } from '../contexts/AuthContext';

/**
 * Define module access rules based on user roles
 */
const MODULE_ACCESS: Record<ModuleId, UserRole[]> = {
  // Command Cube
  dashboard: ['SUPER_ADMIN', 'FAMILY', 'INVESTOR', 'AFFILIATE', 'CUSTOMER'],
  control: ['SUPER_ADMIN'],
  vault: ['SUPER_ADMIN', 'FAMILY'],
  ai: ['SUPER_ADMIN', 'FAMILY', 'INVESTOR', 'AFFILIATE'],
  investor: ['SUPER_ADMIN', 'INVESTOR'],
  funnels: ['SUPER_ADMIN', 'AFFILIATE', 'CUSTOMER'],
  
  // Legacy Cube
  brittany: ['SUPER_ADMIN', 'FAMILY'],
  'legacy-projects': ['SUPER_ADMIN', 'FAMILY'],
  'family-vault': ['SUPER_ADMIN', 'FAMILY'],
  'education-center': ['SUPER_ADMIN', 'FAMILY', 'CUSTOMER'],
  'creator-studio': ['SUPER_ADMIN', 'FAMILY', 'AFFILIATE'],
  'future-planning': ['SUPER_ADMIN', 'FAMILY'],
  
  // Growth Cube
  search: ['SUPER_ADMIN', 'FAMILY', 'INVESTOR', 'AFFILIATE', 'CUSTOMER'],
  'affiliate-tools': ['SUPER_ADMIN', 'AFFILIATE'],
  'product-builder': ['SUPER_ADMIN', 'AFFILIATE'],
  analytics: ['SUPER_ADMIN', 'INVESTOR', 'AFFILIATE'],
  campaigns: ['SUPER_ADMIN', 'AFFILIATE'],
  'marketing-systems': ['SUPER_ADMIN', 'AFFILIATE'],
  'opportunity-radar': ['SUPER_ADMIN'],
};

/** Modules gated by paid entitlement for CUSTOMER users. Payment never changes roles. */
export const PAID_MODULES: ModuleId[] = ['dashboard', 'ai'];

/**
 * Check if a user has access to a specific module.
 * entitledModules semantics for CUSTOMER paid modules (fail closed):
 *   null  = entitlements still loading or failed to load → locked
 *   []    = loaded, no entitlements → locked
 *   [...] = loaded → allowed only if module included
 */
export function canAccessModule(
  moduleId: ModuleId,
  userRole: UserRole,
  entitledModules: string[] | null = null
): boolean {
  if (userRole === 'CUSTOMER' && PAID_MODULES.includes(moduleId)) {
    return entitledModules !== null && entitledModules.includes(moduleId);
  }
  const allowedRoles = MODULE_ACCESS[moduleId];
  return allowedRoles ? allowedRoles.includes(userRole) : false;
}

/**
 * Get the reason why access is denied
 */
export function getAccessDeniedMessage(
  moduleId: ModuleId,
  userRole: UserRole,
  entitledModules: string[] | null = null
): string {
  if (userRole === 'CUSTOMER' && PAID_MODULES.includes(moduleId)) {
    if (entitledModules === null) {
      return 'Checking your access... If this persists, please try again.';
    }
    return 'This module requires an active package.';
  }
  const allowedRoles = MODULE_ACCESS[moduleId];
  
  if (!allowedRoles || allowedRoles.length === 0) {
    return 'This module is not available yet.';
  }
  
  return `Access denied. Required role: ${allowedRoles.join(', ')}. Your current role: ${userRole}.`;
}

/** True when the locked state is purchasable (CUSTOMER + paid module, entitlements loaded). */
export function isPurchasableLock(
  moduleId: ModuleId,
  userRole: UserRole,
  entitledModules: string[] | null = null
): boolean {
  return (
    userRole === 'CUSTOMER' &&
    PAID_MODULES.includes(moduleId) &&
    entitledModules !== null &&
    !entitledModules.includes(moduleId)
  );
}
